import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  lastInterestMatchedJobsSet,
  lastSkillMatchedJobsSet,
  resetJobMatchState
} from '@pages/job-match/state/actions';
import { generateJobDetail } from '@pages/jobs/utils/jobs-detail.util';
import { noop, of, pipe } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { APP } from 'src/app/app.constant';
import { IUser } from 'src/gen';
import { AuthService } from './auth.service';

export interface AuthState {
  isLoading: boolean;
  jwt: string;
  user: IUser | null;
  occupations: any[];
  feedbackSurveyPassed: number;
  error: string | null;
}

const initialState: AuthState = {
  isLoading: false,
  jwt: '',
  user: null,
  occupations: [],
  feedbackSurveyPassed: 0,
  error: null
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(state => ({
    authInfo: computed(() => state.jwt()),
    isLoading: computed(() => state.isLoading()),
    isAuthenticated: computed(() => !!state.user()),
    feedbackSurveyVisible: computed(() => state.user() && !state.feedbackSurveyPassed()),
    userInfo: computed(() => {
      const user = state.user();
      const occupations = state.occupations();

      if (!user) return null;

      return {
        ...user,
        saved_jobs: user.saved_jobs?.map(job =>
          generateJobDetail(
            job,
            occupations?.filter(occupation => occupation?.onetCode === (job.custom_id || job.onet_code))[0]
          )
        )
      };
    }),
    isEducator: computed(() => {
      const user = state.user();
      if (!user) return null;
      return user.role.name === APP.ROLES.EDUCATOR;
    }),
    isEmployer: computed(() => {
      const user = state.user();
      if (!user) return null;
      return user.role.name === APP.ROLES.EMPLOYER;
    }),
    companyId: computed(() => state.user()?.affiliated_company?.id),
    schoolId: computed(() => state.user()?.affiliated_school?.id),
    userEmail: computed(() => state.user()?.email),
    error: computed(() => state.error())
  })),
  withMethods(
    (
      store,
      reduxStore = inject(Store),
      authService = inject(AuthService),
      localStorageService = inject(LocalStorageService),
      router = inject(Router)
    ) => {
      const fetchOccupationsForUser = (user: IUser, isSyncMatchedJob = false) => {
        const onetCodes = user.saved_jobs
          ?.filter(job => job.custom_id || job.onet_code)
          .map(job => job.custom_id || job.onet_code);

        if (onetCodes && onetCodes.length > 0) {
          authService.fetchOccupations(onetCodes).subscribe(occupationsResponse => {
            patchState(store, { occupations: occupationsResponse.occupations });
          });
        }
        if (isSyncMatchedJob) {
          const {
            interests = [],
            lastInterestMatchedJobs = [],
            skills = [],
            skillsSurvey = {
              occupations: [],
              majors: [],
              occupationsDetails: [],
              selectedOccupation: null,
              takeOnce: false
            }
          } = JSON.parse(user?.job_match || '{}');

          reduxStore.dispatch(
            lastInterestMatchedJobsSet({
              interests,
              lastInterestMatchedJobs
            })
          );
          reduxStore.dispatch(
            lastSkillMatchedJobsSet({
              skills,
              skillsSurvey,
              jobFamilyOccupations: [],
              degreeProgramOccupations: [],
              educationLevelOccupations: [],
              militaryOccupations: []
            })
          );
        }
      };

      function logout() {
        localStorageService.removeItem(APP.RETURN_URL);
        localStorageService.removeItem(APP.AUTH.INFOR);
        patchState(store, initialState);

        reduxStore.dispatch(resetJobMatchState());
        window.location.href = APP.LOGIN_URL;
      }

      function clearError() {
        patchState(store, { error: null });
      }

      function setAuthInfo(jwt: string) {
        patchState(store, { jwt, isLoading: false });
      }

      const register = rxMethod<{ username: string; password: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(({ username, password }) =>
            authService.register(username, password).pipe(
              map(response => {
                // Store JWT in localStorage
                localStorageService.setItem(APP.AUTH.INFOR, response.jwt);

                // Update state
                patchState(store, {
                  jwt: response.jwt,
                  isLoading: false,
                  error: null
                });

                // Fetch user info and navigate
                fetchUserInfo();
                router.navigateByUrl('/profile').then(noop);

                return response;
              }),
              catchError((error: any) => {
                patchState(store, {
                  isLoading: false,
                  error: error?.error?.error?.message || 'Registration failed'
                });
                return of(error);
              })
            )
          )
        )
      );

      const login = rxMethod<{ identifier: string; password: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(({ identifier, password }) =>
            authService.login(identifier, password).pipe(
              map(response => {
                localStorageService.setItem(APP.AUTH.INFOR, response.jwt);

                // Update state
                patchState(store, {
                  jwt: response.jwt,
                  isLoading: false,
                  error: null
                });

                // Fetch user info
                fetchUserInfo();

                // Navigate to return URL or default
                const returnUrl = localStorageService.getItem(APP.RETURN_URL) || APP.RETURN_URL_DEFAULT;
                localStorage.removeItem(APP.RETURN_URL);
                router.navigateByUrl(returnUrl).then(noop);

                return response;
              }),
              catchError(error => {
                patchState(store, {
                  isLoading: false,
                  error: error?.error?.error?.message || 'Login failed'
                });
                return of(error);
              })
            )
          )
        )
      );

      const forgotPassword = rxMethod<{ email: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(({ email }) =>
            authService.forgotPassword(email).pipe(
              map(response => {
                patchState(store, {
                  isLoading: false,
                  error: null
                });
                return response;
              }),
              catchError(error => {
                const errorMessage = error?.error?.error?.message || 'Something went wrong';
                patchState(store, {
                  isLoading: false,
                  error: errorMessage
                });
                return of(error);
              })
            )
          )
        )
      );

      const resetPassword = rxMethod<{ code: string; password: string; passwordConfirmation: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(({ code, password, passwordConfirmation }) =>
            authService.resetPassword(code, password, passwordConfirmation).pipe(
              map(response => {
                patchState(store, {
                  isLoading: false,
                  error: null
                });
                router.navigateByUrl('/auth/login').then(noop);
                return response;
              }),
              catchError(error => {
                const errorMessage = error.error?.message?.[0]?.messages?.[0]?.message || 'Something went wrong';
                patchState(store, {
                  isLoading: false,
                  error: errorMessage
                });
                return of(error);
              })
            )
          )
        )
      );

      const fetchUserInfo = rxMethod<void>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(() =>
            authService.fetchUserInfo().pipe(
              map(response => {
                patchState(store, {
                  user: response.user,
                  feedbackSurveyPassed: response.feedbackSurveyPassed,
                  isLoading: false,
                  error: null
                });

                fetchOccupationsForUser(response.user, true);

                return response;
              }),
              catchError(error => {
                patchState(store, {
                  isLoading: false,
                  error: error.error?.error?.message || 'Failed to fetch user info'
                });
                return of(error);
              })
            )
          )
        )
      );

      const updateUserInfo = rxMethod<{ id: string; data: any }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true, error: null });
          }),
          switchMap(({ id, data }) =>
            authService.updateUserInfo(id, data).pipe(
              map(response => {
                patchState(store, {
                  user: response.user,
                  isLoading: false,
                  error: null
                });

                fetchOccupationsForUser(response.user);

                return response;
              }),
              catchError(error => {
                patchState(store, {
                  isLoading: false,
                  error: error.error?.error?.message || 'Failed to update user info'
                });
                return of(error);
              })
            )
          )
        )
      );

      return {
        logout,
        login,
        register,
        fetchUserInfo,
        updateUserInfo,
        setAuthInfo,
        forgotPassword,
        resetPassword,
        clearError
      };
    }
  )
);
